import { forwardRef, useState, useRef, useEffect, useMemo } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Divider } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  isNative,
  useOverlayLayer,
  LAYER_OFFSET,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import { useToggletip } from "./useToggletip";
import type { ToggletipPlacement, ToggletipProps } from "./types";

const getPositionStyles = (placement: ToggletipPlacement, offset: number) => {
  switch (placement) {
    case "bottom":
      return {
        top: `calc(100% + ${offset}px)`,
        left: "50%",
        transform: "translateX(-50%)",
      };
    case "bottom-left":
      return { top: `calc(100% + ${offset}px)`, left: 0 };
    case "bottom-right":
      return { top: `calc(100% + ${offset}px)`, right: 0 };
    case "top":
      return {
        bottom: `calc(100% + ${offset}px)`,
        left: "50%",
        transform: "translateX(-50%)",
      };
    case "top-left":
      return { bottom: `calc(100% + ${offset}px)`, left: 0 };
    case "top-right":
      return { bottom: `calc(100% + ${offset}px)`, right: 0 };
    case "left":
      return {
        right: `calc(100% + ${offset}px)`,
        top: "50%",
        transform: "translateY(-50%)",
      };
    case "right":
      return {
        left: `calc(100% + ${offset}px)`,
        top: "50%",
        transform: "translateY(-50%)",
      };
    default:
      return {
        bottom: `calc(100% + ${offset}px)`,
        left: "50%",
        transform: "translateX(-50%)",
      };
  }
};

export const Toggletip = forwardRef<any, ToggletipProps & ThemeOverrideProps>(
  (
    {
      children,
      title,
      content,
      footer,
      width = "288px",
      direction = "top",
      autoDirection = true,
      offset = 12,
      "data-testid": dataTestId,
      testID,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const [isOpen, setOpen] = useState(false);
    const containerRef = useRef<HTMLElement | null>(null);
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const overlayLayer = useOverlayLayer();

    const { refs, direction: newDirection } = useToggletip({
      open: isOpen,
      direction,
      offset,
      autoDirection,
    });

    const toggleOpen = () => setOpen(!isOpen);

    useEffect(() => {
      if (!isOpen || isNative) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          event.target instanceof Node &&
          !containerRef.current.contains(event.target)
        ) {
          setOpen(false);
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }, [isOpen]);

    const positionStyles = useMemo(
      () => getPositionStyles(newDirection, offset),
      [newDirection, offset]
    );

    return (
      <Box
        testID={testID}
        ref={(node: HTMLElement | null) => {
          containerRef.current = node;
          refs.setReference(node);
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as any).current = node;
        }}
        display="inline-flex"
        position="relative"
        style={{ height: "fit-content" }}
      >
        <Box onPress={toggleOpen} style={{ cursor: "pointer" }}>
          {children}
        </Box>
        {isOpen && (
          <Box
            ref={refs.setFloating}
            data-testid={dataTestId}
            position="absolute"
            backgroundColor={theme.colors.background.primary}
            borderRadius={theme.shape.tooltip.md.borderRadius}
            borderColor={theme.colors.border.secondary}
            padding={12}
            width={width}
            zIndex={overlayLayer + LAYER_OFFSET.tooltip}
            style={{
              ...positionStyles,
              boxShadow: theme.shadow.popover,
            }}
          >
            {title && (
              <Box gap={8}>
                <Text
                  color={theme.colors.content.primary}
                  fontSize={24}
                  lineHeight="28px"
                  fontWeight="500"
                >
                  {title}
                </Text>
                <Divider color={theme.colors.border.secondary} />
              </Box>
            )}
            {content && (
              <Box marginBottom={16} marginTop={16}>
                {typeof content === "string" || typeof content === "number" ? (
                  <Text
                    color={theme.colors.content.primary}
                    fontSize={14}
                    fontWeight="400"
                  >
                    {content}
                  </Text>
                ) : (
                  content
                )}
              </Box>
            )}
            {footer && (
              <Box
                marginTop={20}
                flexDirection="row"
                justifyContent="flex-end"
                gap={12}
              >
                {footer}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

Toggletip.displayName = "Toggletip";
